<template>
  <section>
    <header
      :class="{ 'is-active': open }"
      v-on:click="open = !open"
    >
      <i class="icon-down-open"></i>
      <p>
        <span class="title is-4">{{ fieldDefinition.display_name }}</span>
        <span
          class="loader is-inline-block ml-2"
          v-if="fieldData.loading"
        ></span>
      </p>
      <p
        class="subtitle"
        v-if="fieldDefinition.help_text"
      >
        {{ fieldDefinition.help_text }}
      </p>
    </header>
    <template v-if="open">
      <p
        class="help is-danger"
        v-for="(error, i) in fieldData.errors"
        :key="i"
      >
        {{ error }}
      </p>
    </template>
    <div
      class="field"
      v-show="open"
      v-for="childDefinition in visibleChildren"
      :key="`${configurationStore.originalConfigurationId}-${childDefinition.key}`"
    >
      <label class="label">{{ childDefinition.display_name }}</label>
      <p v-if="childDefinition.help_text">
        {{ childDefinition.help_text }}
      </p>
      <div class="control">
        <component
          :is="childDefinition.many ? ListField : FIELD_COMPONENTS[childDefinition.type]"
          :field-key="`${fieldDefinition.key}.${childDefinition.key}`"
          :field-definition="childDefinition"
          :worker-id="workerId"
          :corpus-id="corpusId"
          v-model="childData[childDefinition.key]"
        />
      </div>
    </div>

    <hr :class="{ 'mt-2': !open }" />
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useWorkerConfigurationStore } from '@/stores'
import { type WorkerConfigurationField } from '@/types/workerConfiguration'

import { FIELD_COMPONENTS, type FieldModel, type FieldProps } from '.'
import ListField from './ListField.vue'

type Constraint = { type: 'group'; many: false }
const {
  fieldDefinition,
  workerId,
  corpusId,
  readonly = false,
} = defineProps<FieldProps<Constraint> & { readonly?: boolean }>()
const fieldData = defineModel<Omit<FieldModel<Constraint>, 'value'>>({ required: true })

const configurationStore = useWorkerConfigurationStore()

const open = ref(false)

const visibleChildren = computed<WorkerConfigurationField[]>(() => {
  if (readonly)
    return fieldDefinition.children.map(
      (child) =>
        ({
          ...child,
          editable: false,
          default:
            child.default ??
            configurationStore.editedConfiguration[`${fieldDefinition.key}.${child.key}`].value,
        }) as WorkerConfigurationField,
    )
  return fieldDefinition.children.filter((child) => child.editable)
})

const childData = computed(() =>
  Object.fromEntries(
    visibleChildren.value.map(({ key }) => [
      key,
      configurationStore.editedConfiguration[`${fieldDefinition.key}.${key}`],
    ]),
  ),
)

// Have the group's `loading` and `valid` attributes depend on the state of every child field
const anyChildLoading = computed(() =>
  Object.values(childData.value).some(({ loading }) => loading),
)
const allChildrenValid = computed(() => Object.values(childData.value).every(({ valid }) => valid))
watch(
  anyChildLoading,
  (newValue: boolean) => {
    fieldData.value.loading = newValue
  },
  { immediate: true },
)
watch(
  allChildrenValid,
  (newValue: boolean) => {
    fieldData.value.valid = newValue
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
header {
  margin-top: -0.25em;
  margin-bottom: -0.25em;
  padding-top: 0.25em;
  padding-bottom: 0.25em;
  cursor: pointer;

  &:hover {
    background-color: var(--bulma-scheme-main-ter);
  }

  &.is-active {
    margin-bottom: 0.5em;
  }
}

.icon-down-open {
  margin-top: 0.25em;
  margin-right: 0.25em;
  float: left;

  &::before {
    transform: rotate(-90deg);
    transition: transform 0.2s;

    .is-active > & {
      transform: none;
    }
  }
}

label.label + p {
  /* Move the default label margin to below the help text instead, so that the display name and help text are grouped together */
  margin-top: calc(-1 * var(--bulma-label-spacing));
  margin-bottom: var(--bulma-label-spacing);
}
</style>
